import React, { useState, useEffect } from 'react';
import { GitBranch, Download, ArrowRight, Plus, Edit3, X, Trash2, CheckSquare, Square, Search, Eye } from 'lucide-react';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import { useAuth } from '../context/AuthContext';
import DependencyDrawer from '../components/DependencyDrawer';
import TrackingItemDetailDrawer from '../components/TrackingItemDetailDrawer';
import { ENTITY_COLORS, ENTITY_ICONS } from '../components/EntityPicker';
import TrackingFilterBar from '../components/filters/TrackingFilterBar';

const DependenciesPage = () => {
  const { filters: globalFilters, getFilterParams } = useFilters();
  const { user } = useAuth();
  const [dependencies, setDependencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'graph'
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingDependency, setEditingDependency] = useState(null);
  const [selectedDependency, setSelectedDependency] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Detail drawer with comments
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [viewingDependency, setViewingDependency] = useState(null);
  
  // Filter Bar 2.0 state
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [quickFilters, setQuickFilters] = useState({});
  const [filterCounts, setFilterCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Load dependencies when filters change
  useEffect(() => { loadDependencies(); }, [advancedFilters, quickFilters, searchTerm]);

  const loadDependencies = async () => {
    try {
      setLoading(true);
      
      // Build params for the new filter API
      const params = {
        // Multi-select filters
        ...(advancedFilters.status?.length > 0 && { status: advancedFilters.status.join(',') }),
        ...(advancedFilters.severity?.length > 0 && { severity: advancedFilters.severity.join(',') }),
        ...(advancedFilters.dep_type?.length > 0 && { dep_type: advancedFilters.dep_type.join(',') }),
        ...(advancedFilters.owner?.length > 0 && { owner: advancedFilters.owner.join(',') }),
        ...(advancedFilters.portfolio?.length > 0 && { portfolio: advancedFilters.portfolio.join(',') }),
        ...(advancedFilters.program?.length > 0 && { program: advancedFilters.program.join(',') }),
        ...(advancedFilters.project?.length > 0 && { project: advancedFilters.project.join(',') }),
        
        // Date ranges
        ...(advancedFilters.due_date_start && { due_date_start: advancedFilters.due_date_start }),
        ...(advancedFilters.due_date_end && { due_date_end: advancedFilters.due_date_end }),
        ...(advancedFilters.raised_date_start && { raised_date_start: advancedFilters.raised_date_start }),
        ...(advancedFilters.raised_date_end && { raised_date_end: advancedFilters.raised_date_end }),
        
        // Quick filters
        ...quickFilters,
        
        // Search
        ...(searchTerm && { search: searchTerm }),
        
        // Current user for "my items" filter
        ...(user?.email && { current_user: user.email }),
      };
      
      const res = await api.filterDependencies(params);
      setDependencies(res.data.dependencies || []);
      setFilterCounts(res.data.counts || {});
    } catch (error) { 
      console.error('Failed to load dependencies:', error); 
      // Fallback to governance API
      try {
        const res = await api.listDependencies({});
        setDependencies(res.data?.data?.dependencies || []);
      } catch (e) {
        setDependencies([]);
      }
    }
    finally { setLoading(false); }
  };

  const handleCreateNew = () => {
    setEditingDependency(null);
    setDrawerOpen(true);
  };

  const handleEdit = (dependency) => {
    setEditingDependency(dependency);
    setDrawerOpen(true);
  };

  const handleDrawerSuccess = () => {
    loadDependencies();
    setSelectedDependency(null);
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === dependencies.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(dependencies.map(d => d.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length || !window.confirm(`Delete ${selectedIds.length} selected dependencies?`)) return;
    setBulkLoading(true);
    try {
      await api.bulkDeleteDependencies(selectedIds);
      setSelectedIds([]);
      loadDependencies();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      alert('Failed to delete dependencies');
    }
    setBulkLoading(false);
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (!selectedIds.length) return;
    setBulkLoading(true);
    try {
      await api.bulkUpdateDependenciesStatus(selectedIds, newStatus);
      setSelectedIds([]);
      loadDependencies();
    } catch (error) {
      console.error('Bulk status update failed:', error);
      alert('Failed to update status');
    }
    setBulkLoading(false);
  };

  const getTypeColor = (type) => {
    const colors = { 'blocks': 'bg-red-100 text-red-800', 'waiting-on': 'bg-yellow-100 text-yellow-800', 'relates': 'bg-blue-100 text-blue-800' };
    return colors[type] || 'bg-[var(--clr-surface-subtle)] text-gray-800';
  };

  const getAgingColor = (aging) => {
    if (aging > 30) return 'text-[var(--clr-sev-blocked)]';
    if (aging > 14) return 'text-[var(--clr-sev-atrisk)]';
    return 'text-[var(--clr-sev-healthy)]';
  };

  const kpis = {
    total: dependencies.length,
    blocking: filterCounts.blocking || dependencies.filter(d => d.type === 'blocks' || d.kind === 'blocks').length,
    avgAging: dependencies.length > 0 ? Math.round(dependencies.reduce((s, d) => s + (d.aging || 0), 0) / dependencies.length) : 0,
    critical: filterCounts.critical || dependencies.filter(d => d.severity === 'Critical' || d.severity === 'High').length,
    overdue: filterCounts.overdue || 0,
    agingOver30: filterCounts.aging_over_30 || dependencies.filter(d => d.aging > 30).length
  };

  // Simple graph visualization
  const renderGraph = () => {
    const projects = new Set();
    dependencies.forEach(d => { projects.add(d.from_project_name); projects.add(d.to_project_name); });
    const projectList = Array.from(projects);
    
    return (
      <div className="p-4">
        <div className="flex flex-wrap gap-4 justify-center">
          {projectList.slice(0, 20).map((proj, idx) => (
            <div key={idx} className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg text-sm font-medium">
              {proj}
            </div>
          ))}
        </div>
        <div className="mt-8 space-y-2">
          {dependencies.slice(0, 20).map((d, idx) => (
            <div key={idx} className="flex items-center justify-center gap-4 text-sm">
              <span className="bg-[var(--clr-surface-subtle)] px-3 py-1 rounded">{d.from_project_name}</span>
              <div className="flex items-center gap-1 text-[var(--clr-text-muted)]">
                <ArrowRight className="w-4 h-4" />
                <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(d.type)}`}>{d.type || 'relates'}</span>
              </div>
              <span className="bg-[var(--clr-surface-subtle)] px-3 py-1 rounded">{d.to_project_name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <GitBranch className="w-7 h-7 text-teal-600" />
            Dependencies
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Track project dependencies and relationships</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium"
            data-testid="new-dependency-btn"
          >
            <Plus className="w-4 h-4" /> New Dependency
          </button>
          <div className="flex bg-[var(--clr-surface-subtle)] rounded-lg p-1">
            <button onClick={() => setViewMode('table')} className={`px-3 py-1 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow' : ''}`}>Table</button>
            <button onClick={() => setViewMode('graph')} className={`px-3 py-1 rounded text-sm ${viewMode === 'graph' ? 'bg-white shadow' : ''}`}>Graph</button>
          </div>
          <a href={api.exportCSV('dependencies', advancedFilters)} className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      {/* Filter Bar 2.0 */}
      <TrackingFilterBar
        trackingType="dependencies"
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        quickFilters={quickFilters}
        onQuickFiltersChange={setQuickFilters}
        counts={filterCounts}
        currentUser={user?.email}
        showInsightsBar={true}
        className="mb-6"
      />

      {/* Search Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search dependencies by name, description, source, or target..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
            data-testid="dependencies-search"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Dependencies</p><p className="text-2xl font-bold">{kpis.total}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Blocking</p><p className="text-2xl font-bold text-[var(--clr-sev-blocked)]">{kpis.blocking}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Avg Aging (days)</p><p className="text-2xl font-bold text-[var(--clr-sev-atrisk)]">{kpis.avgAging}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Overdue</p><p className="text-2xl font-bold text-[var(--clr-sev-blocked)]">{kpis.overdue}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Aging &gt;30 Days</p><p className="text-2xl font-bold text-[var(--clr-sev-atrisk)]">{kpis.agingOver30}</p></div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedIds.length > 0 && (
        <div className="bg-[var(--clr-brand-primary)] text-white px-4 py-3 rounded-lg mb-4 flex items-center justify-between" data-testid="bulk-actions-toolbar">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="flex items-center gap-3">
            <select 
              onChange={(e) => e.target.value && handleBulkStatusUpdate(e.target.value)}
              className="text-sm bg-white text-gray-800 border rounded px-3 py-1.5"
              disabled={bulkLoading}
              data-testid="bulk-status-select"
            >
              <option value="">Set Status...</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
            </select>
            <button 
              onClick={handleBulkDelete}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              disabled={bulkLoading}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <button 
              onClick={() => setSelectedIds([])}
              className="px-3 py-1.5 bg-white/20 text-sm rounded hover:bg-white/30"
              data-testid="bulk-clear-btn"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="bg-[var(--clr-surface-card)] rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div></div>
        ) : dependencies.length === 0 ? (
          <div className="text-center py-12"><GitBranch className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-[var(--clr-text-secondary)]">No dependencies found</p></div>
        ) : viewMode === 'graph' ? (
          renderGraph()
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--clr-surface-subtle)] border-b">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <button onClick={toggleSelectAll} className="text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)]" data-testid="select-all-checkbox">
                      {selectedIds.length === dependencies.length && dependencies.length > 0 ? (
                        <CheckSquare className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Target</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Severity</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Aging</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dependencies.map((d) => (
                  <tr 
                    key={d.id} 
                    className={`hover:bg-[var(--clr-surface-hover)] cursor-pointer ${selectedIds.includes(d.id) ? 'bg-blue-50' : ''}`}
                    onClick={() => setSelectedDependency(d)}
                    data-testid={`dep-row-${d.id}`}
                  >
                    <td className="px-4 py-3">
                      <button 
                        onClick={(e) => { e.stopPropagation(); toggleSelect(d.id); }}
                        className="text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)]"
                        data-testid={`select-dep-${d.id}`}
                      >
                        {selectedIds.includes(d.id) ? (
                          <CheckSquare className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {d.source_entity_type && (
                          <span className={`px-1.5 py-0.5 text-xs rounded ${ENTITY_COLORS[d.source_entity_type] || 'bg-gray-100 text-gray-700'}`}>
                            {d.source_entity_type}
                          </span>
                        )}
                        <div>
                          <div className="font-medium text-[var(--clr-text-primary)]">{d.source_display || d.from_project_name}</div>
                          {d.from_program_name && <div className="text-xs text-[var(--clr-text-muted)]">{d.from_program_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(d.kind || d.type)}`}>
                        {d.kind || d.type || 'relates'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {d.target_entity_type && (
                          <span className={`px-1.5 py-0.5 text-xs rounded ${ENTITY_COLORS[d.target_entity_type] || 'bg-gray-100 text-gray-700'}`}>
                            {d.target_entity_type}
                          </span>
                        )}
                        <div>
                          <div className="font-medium text-[var(--clr-text-primary)]">{d.target_display || d.to_project_name}</div>
                          {d.to_program_name && <div className="text-xs text-[var(--clr-text-muted)]">{d.to_program_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        d.status?.toLowerCase() === 'open' ? 'bg-blue-100 text-blue-700' :
                        d.status?.toLowerCase() === 'closed' ? 'bg-green-100 text-green-700' :
                        d.status?.toLowerCase() === 'resolved' ? 'bg-green-100 text-green-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {d.status || 'Open'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        d.severity?.toLowerCase() === 'critical' ? 'bg-red-100 text-red-700' :
                        d.severity?.toLowerCase() === 'high' ? 'bg-orange-100 text-orange-700' :
                        d.severity?.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {d.severity || 'Medium'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-bold ${getAgingColor(d.aging)}`}>
                        {d.aging || 0} days
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); setViewingDependency(d); setDetailDrawerOpen(true); }}
                        className="p-1.5 hover:bg-gray-100 rounded text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)]"
                        data-testid={`view-dep-${d.id}`}
                        title="View Details & Comments"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(d); }}
                        className="p-1.5 hover:bg-gray-100 rounded text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)]"
                        data-testid={`edit-dep-${d.id}`}
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dependency Detail Drawer */}
      {selectedDependency && (
        <div className="fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedDependency(null)}></div>
          <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-violet-50 border-b p-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">Dependency Details</h2>
                <p className="text-sm text-[var(--clr-text-muted)]">{selectedDependency.name || 'Dependency'}</p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => { setSelectedDependency(null); handleEdit(selectedDependency); }}
                  className="px-3 py-1.5 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm hover:bg-[var(--clr-brand-primary-hover)] flex items-center gap-1"
                  data-testid="edit-dep-from-drawer"
                >
                  <Edit3 className="w-4 h-4" /> Edit
                </button>
                <button onClick={() => setSelectedDependency(null)} className="p-2 hover:bg-black/10 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Direction Visualization */}
              <div className="flex items-center justify-center gap-4 py-4 bg-[var(--clr-surface-subtle)] rounded-xl">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedDependency.source_entity_type ? ENTITY_COLORS[selectedDependency.source_entity_type]?.split(' ').slice(0,2).join(' ') || 'bg-gray-100' : 'bg-gray-100'}`}>
                  {selectedDependency.source_entity_type && (
                    <span className="text-xs font-medium capitalize">{selectedDependency.source_entity_type}:</span>
                  )}
                  <span className="text-sm font-medium">{selectedDependency.source_display || selectedDependency.from_project_name}</span>
                </div>
                <div className="flex flex-col items-center">
                  <ArrowRight className="w-6 h-6 text-[var(--clr-text-muted)]" />
                  <span className="text-xs text-[var(--clr-text-muted)] capitalize">{selectedDependency.kind || selectedDependency.type || 'relates'}</span>
                </div>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${selectedDependency.target_entity_type ? ENTITY_COLORS[selectedDependency.target_entity_type]?.split(' ').slice(0,2).join(' ') || 'bg-gray-100' : 'bg-gray-100'}`}>
                  {selectedDependency.target_entity_type && (
                    <span className="text-xs font-medium capitalize">{selectedDependency.target_entity_type}:</span>
                  )}
                  <span className="text-sm font-medium">{selectedDependency.target_display || selectedDependency.to_project_name}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)] mb-1">Status</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    selectedDependency.status?.toLowerCase() === 'open' ? 'bg-blue-100 text-blue-700' :
                    selectedDependency.status?.toLowerCase() === 'closed' ? 'bg-green-100 text-green-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedDependency.status || 'Open'}
                  </span>
                </div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)] mb-1">Severity</p>
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                    selectedDependency.severity?.toLowerCase() === 'critical' ? 'bg-red-100 text-red-700' :
                    selectedDependency.severity?.toLowerCase() === 'high' ? 'bg-orange-100 text-orange-700' :
                    selectedDependency.severity?.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {selectedDependency.severity || 'Medium'}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)] mb-1">Raised Date</p>
                  <p className="font-medium">{selectedDependency.raised_date?.split('T')[0] || '-'}</p>
                </div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)] mb-1">Due Date</p>
                  <p className="font-medium">{selectedDependency.due_date?.split('T')[0] || '-'}</p>
                </div>
              </div>
              <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                <p className="text-xs text-[var(--clr-text-muted)] mb-1">Aging</p>
                <p className={`text-xl font-bold ${getAgingColor(selectedDependency.aging)}`}>{selectedDependency.aging || 0} days</p>
              </div>
              <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                <p className="text-xs text-[var(--clr-text-muted)] mb-1">Owner</p>
                <p className="font-medium">{selectedDependency.owner || '-'}</p>
              </div>
              {selectedDependency.description && (
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)] mb-1">Description</p>
                  <p className="text-sm">{selectedDependency.description}</p>
                </div>
              )}
              {selectedDependency.notes && (
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)] mb-1">Notes</p>
                  <p className="text-sm">{selectedDependency.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dependency Create/Edit Drawer */}
      <DependencyDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        dependency={editingDependency}
        onSuccess={handleDrawerSuccess}
      />

      {/* Detail Drawer with Comments */}
      <TrackingItemDetailDrawer
        isOpen={detailDrawerOpen}
        onClose={() => { setDetailDrawerOpen(false); setViewingDependency(null); }}
        item={viewingDependency}
        itemType="dependency"
        tenantId={user?.tenant?.id || user?.tenant_id}
      />
    </div>
  );
};

export default DependenciesPage;
